import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bookmark, Sparkles, Users, CalendarDays, Trophy } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { ideasStore } from "@/lib/storage";
import { OverallScore, ScoreBars } from "@/components/Scores";
import { IdeaInsights } from "@/components/IdeaInsights";
import { SocialShare } from "@/components/SocialShare";
import { shareText } from "@/components/ShareButton";
import { VoteButton } from "@/components/VoteButton";
import { Comments } from "@/components/Comments";
import { getClientId } from "@/lib/account";

const mdLink = { a: (p) => <a {...p} target="_blank" rel="noopener noreferrer" className="text-amber-300 underline underline-offset-2" /> };

const useSharedIdea = (shareId) => {
  const [data, setData] = useState({ idea: null, rank: null, votes: 0, voted: false, missing: false });
  useEffect(() => {
    axios.get(`${API}/shared/${shareId}`, { headers: { "X-Client-Id": getClientId() } })
      .then((r) => setData({ idea: r.data.idea, rank: r.data.rank, votes: r.data.votes, voted: r.data.voted, missing: false }))
      .catch(() => setData((d) => ({ ...d, missing: true })));
  }, [shareId]);
  const applyVote = (v) => setData((d) => ({ ...d, votes: v.votes, voted: v.voted, rank: v.rank }));
  return { ...data, applyVote };
};

const MdBlock = ({ title, icon: Icon, text, testId }) => (
  <section className="rounded-3xl bg-[#12141F] border border-white/[0.07] p-6 sm:p-8" data-testid={testId}>
    <div className="flex items-center gap-3"><Icon className="w-5 h-5 text-amber-400" /><h2 className="font-display text-lg md:text-xl font-bold">{title}</h2></div>
    <div className="md-body mt-6"><ReactMarkdown remarkPlugins={[remarkGfm]} components={mdLink}>{text}</ReactMarkdown></div>
  </section>
);

const Fact = ({ label, value }) => (
  <div className="py-3 border-b border-white/5 last:border-0">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-slate-200 mt-1 leading-relaxed">{value || "—"}</div>
  </div>
);

const MEDAL_CLS = {
  1: "bg-amber-500 font-bold text-slate-950 shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:bg-amber-400",
  2: "bg-slate-300 font-bold text-slate-950 shadow-[0_0_20px_rgba(203,213,225,0.3)] hover:bg-slate-200",
  3: "bg-orange-600 font-bold text-slate-950 shadow-[0_0_20px_rgba(217,119,6,0.3)] hover:bg-orange-500",
};

const RankBadge = ({ rank }) => {
  if (!rank) return null;
  const medal = MEDAL_CLS[rank];
  const testId = rank === 1 ? "shared-winner-badge" : medal ? `shared-medal-${rank}-badge` : "shared-rank-badge";
  return (
    <Link to="/classifica" data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${medal || "border border-white/15 font-semibold text-slate-300 hover:border-amber-500/60 hover:text-amber-300"}`}>
      <Trophy className="w-3.5 h-3.5" /> {medal ? `N.${rank} in classifica` : `#${rank} in classifica`}
    </Link>
  );
};

const SharedHeader = ({ idea, rank, shareId, onSave, votes, voted, onVote }) => (
  <div className="grid lg:grid-cols-12 gap-8">
    <div className="lg:col-span-8">
      <div className="flex flex-wrap items-center gap-3">
        <div className="overline-label">Idea condivisa · {idea.categoria}</div>
        <RankBadge rank={rank} />
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]" data-testid="shared-idea-name">{idea.nome}</h1>
      <p className="text-lg text-slate-300 mt-5 leading-relaxed">{idea.pitch}</p>
      <p className="text-slate-400 mt-4 leading-relaxed">{idea.descrizione}</p>
      <div className="flex flex-wrap gap-3 mt-7">
        <VoteButton shareId={shareId} votes={votes} voted={voted} onChange={onVote} testId="shared-vote-btn" />
        <button onClick={onSave} data-testid="shared-save-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-amber-500/60 hover:text-amber-300 transition-colors"><Bookmark className="w-4 h-4" /> Salva nelle mie idee</button>
      </div>
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2.5">Condividi questa idea</div>
        <SocialShare url={`${process.env.REACT_APP_BACKEND_URL}/api/s/${shareId}`} text={shareText(idea)} prefix="shared-share" />
      </div>
    </div>
    <aside className="lg:col-span-4 rounded-3xl bg-[#12141F] border border-white/[0.07] p-6">
      <div className="flex items-center justify-between"><div className="font-display font-semibold">Valutazione AI</div><OverallScore value={idea.punteggio_complessivo} testId="shared-idea-overall" /></div>
      <div className="mt-5"><ScoreBars scores={idea.punteggi} testPrefix="shared-idea" /></div>
      <p className="mt-5 text-sm italic text-slate-400 border-l-2 border-amber-500/60 pl-3">{idea.giudizio}</p>
    </aside>
  </div>
);

const SharedFacts = ({ idea }) => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="rounded-3xl bg-[#12141F] border border-white/[0.07] px-6 py-3">
      <Fact label="Problema risolto" value={idea.problema} />
      <Fact label="Cliente ideale" value={idea.cliente_ideale} />
      <Fact label="Costi di avvio" value={idea.costi_avvio} />
      <Fact label="Tempo per il primo guadagno" value={idea.tempo_primo_guadagno} />
      <Fact label="Potenziale di guadagno" value={idea.guadagno_potenziale} />
    </div>
    <div className="rounded-3xl bg-[#12141F] border border-white/[0.07] p-6">
      <div className="font-display font-semibold">Come si guadagna</div>
      <div className="mt-4 space-y-3">
        {(idea.modelli_guadagno || []).map((m) => (
          <div key={m.tipo + m.dettaglio} className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15 p-4">
            <div className="text-sm font-semibold text-emerald-300">{m.tipo}</div>
            <div className="text-sm text-slate-300 mt-1 leading-relaxed">{m.dettaglio}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SharedCta = () => (
  <section className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.1] to-transparent p-8 sm:p-12" data-testid="shared-cta">
    <h2 className="font-display text-2xl sm:text-3xl font-bold">Vuoi idee come questa, su misura per te?</h2>
    <p className="text-slate-400 mt-3 max-w-xl">IdeaForge genera idee di business con punteggi onesti, analisi dei concorrenti e un piano a 30 giorni. Le prime 3 generazioni al giorno sono gratis.</p>
    <Link to="/genera" data-testid="shared-cta-btn" className="inline-flex items-center gap-2 mt-7 rounded-full bg-amber-500 px-7 py-3.5 font-semibold text-slate-950 hover:bg-amber-400 transition-colors"><Sparkles className="w-4 h-4" /> Genera le mie idee gratis</Link>
  </section>
);

export default function SharedIdea() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { idea, rank, votes, voted, missing, applyVote } = useSharedIdea(shareId);

  const save = () => {
    const id = `shared-${shareId}`;
    ideasStore.upsert([{ ...idea, id, saved: true }]);
    toast.success("Idea salvata in Le mie idee");
    navigate(`/idea/${id}`);
  };

  if (missing) return <div className="text-center pt-24" data-testid="shared-not-found"><h1 className="font-display text-3xl font-bold">Link non valido</h1><Link to="/genera" className="inline-block mt-6 text-amber-300">Genera le tue idee</Link></div>;
  if (!idea) return <div className="text-center text-slate-400 pt-24" data-testid="shared-loading">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-8" data-testid="shared-idea-page">
      <SharedHeader idea={idea} rank={rank} shareId={shareId} onSave={save} votes={votes} voted={voted} onVote={applyVote} />
      <SharedFacts idea={idea} />
      <IdeaInsights idea={idea} />
      {idea.competitors && <MdBlock title="Analisi concorrenti" icon={Users} text={idea.competitors} testId="shared-competitors" />}
      {idea.plan && <MdBlock title="Piano d'azione — primi 30 giorni" icon={CalendarDays} text={idea.plan} testId="shared-plan" />}
      <Comments shareId={shareId} />
      <SharedCta />
    </div>
  );
}
